import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, Trash2, Undo2, Redo2, Save, Columns3, Search, Loader2, AlertTriangle, Copy,
  MoreVertical, CheckCircle2, EyeOff, RotateCcw, Layers,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { inr, fmtDate } from "@/lib/api";

const COLS = [
  { key: "date", label: "Date", w: "w-28", edit: "date" },
  { key: "value_date", label: "Value Date", w: "w-28", edit: "date" },
  { key: "description", label: "Description", w: "min-w-[260px]", edit: "text" },
  { key: "transaction_type", label: "Type", w: "w-32", edit: "type" },
  { key: "category", label: "Category", w: "w-40", edit: "category" },
  { key: "reference", label: "Reference", w: "w-28", edit: "text" },
  { key: "cheque_no", label: "Cheque No", w: "w-24", edit: "text" },
  { key: "debit", label: "Debit", w: "w-28", edit: "number", right: true },
  { key: "credit", label: "Credit", w: "w-28", edit: "number", right: true },
  { key: "balance", label: "Balance", w: "w-32", edit: "number", right: true },
  { key: "confidence", label: "Confidence", w: "w-24" },
  { key: "source_file", label: "Source File", w: "w-40" },
  { key: "source_page", label: "Page", w: "w-16" },
];

const TYPES = ["UPI", "NEFT", "RTGS", "IMPS", "ATM Withdrawal", "Cash Deposit", "Cash Withdrawal", "Cheque",
  "EMI", "Loan Disbursement", "Loan Repayment", "Interest", "Bank Charges", "Salary", "Transfer",
  "Insurance", "Investment", "Tax", "GST", "Rent", "Utilities", "Merchant Payment", "Other"];

const CATEGORIES = {
  Income: ["Salary", "Business Income", "Interest Income", "Other Income"],
  "Business Expenses": ["Supplier Payment", "Inventory", "Rent", "Utilities", "Marketing", "Travel",
    "Professional Fees", "Other Business Expense"],
  "Personal Expenses": ["Food", "Shopping", "Entertainment", "Travel", "Medical", "Education",
    "Household", "Other Personal"],
  Financial: ["EMI", "Loan Repayment", "Insurance", "Investment", "Bank Charges", "Interest", "Tax"],
  Transfers: ["Self Transfer", "Bank Transfer", "UPI Transfer"],
};
const FLAT_CATEGORIES = Object.entries(CATEGORIES)
  .flatMap(([g, list]) => list.map((c) => ({ c, g })))
  .filter((x, i, arr) => arr.findIndex((y) => y.c === x.c) === i);

const ROW_H = 38;
const VIEWPORT_H = 560;
const OVERSCAN = 10;
const MOBILE_CHUNK = 50;

export const TransactionTable = ({ transactions, onSave, saving, onBulkExcludeDuplicates, onBulkRestore, bulkBusy }) => {
  const [rows, setRows] = useState(transactions);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [cat, setCat] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");
  const [sort, setSort] = useState({ key: "date", dir: "asc" });
  const [status, setStatus] = useState("all");
  const [scrollTop, setScrollTop] = useState(0);
  const [mobileCount, setMobileCount] = useState(MOBILE_CHUNK);
  const [reason, setReason] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const scrollRef = useRef(null);
  const [visible, setVisible] = useState(COLS.map((c) => c.key));
  const dirty = JSON.stringify(rows) !== JSON.stringify(transactions);

  useEffect(() => {
    setRows(transactions);
    setHistory([]);
    setFuture([]);
  }, [transactions]);

  const commit = (next) => {
    setHistory((h) => [...h.slice(-40), rows]);
    setFuture([]);
    setRows(next);
  };
  const undo = () => {
    if (!history.length) return;
    setFuture((f) => [rows, ...f]);
    setRows(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };
  const redo = () => {
    if (!future.length) return;
    setHistory((h) => [...h, rows]);
    setRows(future[0]);
    setFuture((f) => f.slice(1));
  };

  const setCell = (id, key, value) => {
    commit(rows.map((r) => {
      if (r.id !== id) return r;
      const next = { ...r, [key]: value };
      if (key === "category") {
        const found = FLAT_CATEGORIES.find((x) => x.c === value);
        next.category_group = found?.g || r.category_group;
      }
      return next;
    }));
  };

  const resolve = (id, action) => {
    commit(rows.map((r) => {
      if (r.id !== id) return r;
      if (action === "keep") return { ...r, duplicate_status: "keep", excluded: false };
      if (action === "duplicate") return { ...r, duplicate_status: "duplicate" };
      if (action === "exclude") return { ...r, excluded: true };
      return { ...r, excluded: false, duplicate_status: null };
    }));
  };

  const addRow = () => {
    commit([...rows, {
      id: `manual-${Date.now()}`, date: "", value_date: "", description: "", narration: "",
      reference: "", cheque_no: "", transaction_type: "Other", category: "Other Personal",
      category_group: "Personal Expenses", debit: 0, credit: 0, balance: null,
      source_file: "manual entry", source_page: 1, confidence: 1, confidence_band: "High",
      needs_review: [], flags: [], excluded: false, duplicate_status: null,
    }]);
  };

  const filtered = useMemo(() => {
    let out = rows.filter((r) => {
      if (q) {
        const blob = `${r.description} ${r.reference} ${r.category} ${r.transaction_type}`.toLowerCase();
        if (!blob.includes(q.toLowerCase())) return false;
      }
      if (type !== "all" && r.transaction_type !== type) return false;
      if (cat !== "all" && r.category !== cat) return false;
      if (from && (r.date || "") < from) return false;
      if (to && (r.date || "") > to) return false;
      const amt = Math.max(Number(r.debit) || 0, Number(r.credit) || 0);
      if (minAmt && amt < Number(minAmt)) return false;
      if (maxAmt && amt > Number(maxAmt)) return false;
      if (status === "excluded" && !r.excluded) return false;
      if (status === "included" && r.excluded) return false;
      if (status === "duplicates" && !(r.duplicate_of || r.duplicate_status === "duplicate")) return false;
      return true;
    });
    out = [...out].sort((a, b) => {
      const x = a[sort.key], y = b[sort.key];
      const cmp = typeof x === "number" || typeof y === "number"
        ? (Number(x) || 0) - (Number(y) || 0)
        : String(x ?? "").localeCompare(String(y ?? ""));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, type, cat, from, to, minAmt, maxAmt, sort, status]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
    setMobileCount(MOBILE_CHUNK);
  }, [q, type, cat, from, to, minAmt, maxAmt, sort, status]);

  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(filtered.length, Math.ceil((scrollTop + VIEWPORT_H) / ROW_H) + OVERSCAN);
  const pageRows = filtered.slice(start, end);
  const mobileRows = filtered.slice(0, mobileCount);
  const excludedCount = rows.filter((r) => r.excluded).length;
  const duplicateRows = rows.filter((r) => r.duplicate_of || r.duplicate_status === "duplicate");
  const excludableDuplicates = duplicateRows.filter((r) => !r.excluded).length;
  const cols = COLS.filter((c) => visible.includes(c.key));

  const cellClass = (r) =>
    `${r.confidence_band === "Low" ? "bg-warn" : ""} ${r.needs_review?.length ? "outline outline-1 outline-warn-border" : ""} ${r.excluded ? "line-through opacity-60" : ""}`;

  const rowActions = (r) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`row-actions-${r.id}`} title="Resolve / exclude">
          <MoreVertical className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white/90 backdrop-blur-xl">
        <DropdownMenuLabel className="text-xs">Duplicate resolution</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => resolve(r.id, "keep")} data-testid={`keep-${r.id}`}>
          <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" /> Keep
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => resolve(r.id, "duplicate")} data-testid={`mark-duplicate-${r.id}`}>
          <Copy className="mr-2 h-4 w-4 text-amber-600" /> Mark as Duplicate
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {r.excluded ? (
          <DropdownMenuItem onClick={() => resolve(r.id, "restore")} data-testid={`restore-${r.id}`}>
            <RotateCcw className="mr-2 h-4 w-4 text-primary" /> Restore to Analysis
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => resolve(r.id, "exclude")} data-testid={`exclude-${r.id}`}>
            <EyeOff className="mr-2 h-4 w-4 text-red-600" /> Exclude from Analysis
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => commit(rows.filter((x) => x.id !== r.id))}
                          data-testid={`delete-row-${r.id}`}>
          <Trash2 className="mr-2 h-4 w-4 text-red-600" /> Delete row
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const statusBadge = (r) => {
    if (r.excluded)
      return <Badge variant="outline" className="border-red-200 bg-red-50 text-[10px] text-red-700"
                    data-testid={`excluded-badge-${r.id}`}>Excluded</Badge>;
    if (r.duplicate_status === "duplicate")
      return <Badge variant="outline" className="border-warn-border bg-warn text-[10px] text-warn-foreground"
                    data-testid={`duplicate-badge-${r.id}`}>Duplicate</Badge>;
    if (r.duplicate_status === "keep")
      return <Badge variant="outline" className="border-green-200 bg-ok text-[10px] text-ok-foreground"
                    data-testid={`kept-badge-${r.id}`}>Kept</Badge>;
    if (r.duplicate_of)
      return <Badge variant="outline" className="border-warn-border bg-warn text-[10px] text-warn-foreground"
                    data-testid={`potential-duplicate-badge-${r.id}`}>Potential duplicate</Badge>;
    return null;
  };

  const editor = (r, col) => {
    if (col.edit === "type" || col.edit === "category") {
      const options = col.edit === "type" ? TYPES : FLAT_CATEGORIES.map((x) => x.c);
      return (
        <Select value={r[col.key] || ""} onValueChange={(v) => setCell(r.id, col.key, v)}>
          <SelectTrigger className="h-7 border-0 bg-transparent px-1 text-xs shadow-none focus:ring-1"
                         data-testid={`cell-${col.key}-${r.id}`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {options.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (!col.edit) {
      if (col.key === "confidence")
        return (
          <Badge variant="outline" className={
            r.confidence_band === "Low" ? "border-warn-border bg-warn text-warn-foreground"
              : r.confidence_band === "Medium" ? "border-blue-200 bg-secondary text-secondary-foreground"
                : "border-green-200 bg-ok text-ok-foreground"}>
            {Math.round((r.confidence || 0) * 100)}%
          </Badge>
        );
      return <span className="text-xs text-muted-foreground">{r[col.key] ?? "—"}</span>;
    }
    return (
      <input
        className={`h-7 px-1 text-xs ${col.right ? "text-right num" : ""}`}
        type={col.edit === "date" ? "date" : col.edit === "number" ? "number" : "text"}
        value={col.edit === "number" && !r[col.key] ? "" : (r[col.key] ?? "")}
        onChange={(e) => setCell(r.id, col.key,
          col.edit === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        data-testid={`cell-${col.key}-${r.id}`}
      />
    );
  };

  return (
    <div className="space-y-4" data-testid="transaction-table-wrap">
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search transactions…"
                 className="bg-white pl-9" data-testid="txn-search-input" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-40 bg-white" data-testid="txn-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All types</SelectItem>
            {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-44 bg-white" data-testid="txn-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All categories</SelectItem>
            {FLAT_CATEGORIES.map(({ c }) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid="txn-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="included">Included only</SelectItem>
            <SelectItem value="excluded">Excluded only</SelectItem>
            <SelectItem value="duplicates">Duplicates</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-36 bg-white"
               data-testid="txn-date-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-36 bg-white"
               data-testid="txn-date-to" />
        <Input type="number" placeholder="Min ₹" value={minAmt} onChange={(e) => setMinAmt(e.target.value)}
               className="w-24 bg-white" data-testid="txn-min-amount" />
        <Input type="number" placeholder="Max ₹" value={maxAmt} onChange={(e) => setMaxAmt(e.target.value)}
               className="w-24 bg-white" data-testid="txn-max-amount" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="bg-white" data-testid="column-visibility-btn">
              <Columns3 className="mr-2 h-4 w-4" /> Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white/90 backdrop-blur-xl">
            {COLS.map((c) => (
              <DropdownMenuCheckboxItem key={c.key} checked={visible.includes(c.key)}
                onCheckedChange={(v) => setVisible(v ? [...visible, c.key] : visible.filter((k) => k !== c.key))}>
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="bg-white" onClick={addRow} data-testid="add-row-btn">
          <Plus className="mr-1 h-4 w-4" /> Add Row
        </Button>
        <Button size="sm" variant="outline" className="bg-white" onClick={undo} disabled={!history.length}
                data-testid="undo-btn"><Undo2 className="mr-1 h-4 w-4" /> Undo</Button>
        <Button size="sm" variant="outline" className="bg-white" onClick={redo} disabled={!future.length}
                data-testid="redo-btn"><Redo2 className="mr-1 h-4 w-4" /> Redo</Button>
        <Button size="sm" onClick={() => onSave(rows, reason)} disabled={saving || !dirty} data-testid="save-transactions-btn">
          {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
          Save &amp; Recalculate
        </Button>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={280}
               placeholder="Reason for changes (optional)" className="h-9 w-56 bg-white"
               data-testid="change-reason-input" />
        <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="bg-white" disabled={!duplicateRows.length || bulkBusy}
                    data-testid="exclude-all-duplicates-btn">
              {bulkBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Layers className="mr-1 h-4 w-4" />}
              Exclude All Duplicates ({duplicateRows.length})
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-white/90 backdrop-blur-xl" data-testid="bulk-exclude-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Exclude all detected duplicates?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm">
                  <span className="block" data-testid="bulk-duplicate-count">
                    <b>{duplicateRows.length}</b> duplicate transaction(s) detected —{" "}
                    <b>{excludableDuplicates}</b> will be excluded from analysis
                    {duplicateRows.length - excludableDuplicates > 0
                      ? ` (${duplicateRows.length - excludableDuplicates} already excluded)` : ""}.
                  </span>
                  <span className="block">
                    Nothing is deleted. Excluded transactions stay visible in the table, are removed from all
                    totals and analytics, and can be restored at any time. Every change is written to the audit trail.
                  </span>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={280}
                         placeholder="Reason (optional)" data-testid="bulk-reason-input" />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="bulk-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onBulkExcludeDuplicates?.(reason)} data-testid="bulk-confirm-btn">
                Exclude {excludableDuplicates} transaction(s)
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {excludedCount > 0 && (
          <Button size="sm" variant="outline" className="bg-white" disabled={bulkBusy}
                  onClick={() => onBulkRestore?.(reason)} data-testid="restore-all-btn">
            <RotateCcw className="mr-1 h-4 w-4" /> Restore All Excluded ({excludedCount})
          </Button>
        )}
        <span className="text-xs text-muted-foreground" data-testid="txn-count-label">
          {filtered.length} of {rows.length} transactions
          {excludedCount ? ` · ${excludedCount} excluded from analysis` : ""}
          {dirty ? " · unsaved changes" : ""}
        </span>
      </div>

      {/* desktop virtualized spreadsheet */}
      <div className="hidden rounded-lg border border-border bg-white md:block">
        <div ref={scrollRef} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
             style={{ height: VIEWPORT_H }} className="overflow-auto"
             data-testid="transaction-table-viewport">
          <table className="w-full border-collapse text-left text-sm" data-testid="transaction-table">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                <th className="w-8 border-b border-border px-2 py-2" />
                {cols.map((c) => (
                  <th key={c.key}
                      onClick={() => setSort({ key: c.key, dir: sort.key === c.key && sort.dir === "asc" ? "desc" : "asc" })}
                      className={`cursor-pointer border-b border-border px-2 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${c.w} ${c.right ? "text-right" : ""}`}
                      data-testid={`sort-${c.key}`}>
                    {c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
                  </th>
                ))}
                <th className="w-24 border-b border-border px-2 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Status
                </th>
                <th className="w-10 border-b border-border" />
              </tr>
            </thead>
            <tbody>
              {start > 0 && <tr style={{ height: start * ROW_H }} aria-hidden />}
              {pageRows.map((r) => (
                <tr key={r.id} style={{ height: ROW_H }}
                    className={`border-b border-border hover:bg-muted/40 ${r.excluded ? "bg-red-50/40" : ""}`}
                    data-testid={`txn-row-${r.id}`}>
                  <td className="px-2">
                    {r.duplicate_of && <Copy className="h-3 w-3 text-amber-600" title="Potential duplicate" />}
                    {r.flags?.includes("Balance Mismatch") && <AlertTriangle className="h-3 w-3 text-red-600" title="Balance mismatch" />}
                  </td>
                  {cols.map((c) => (
                    <td key={c.key} className={`sheet-cell border-r border-border/60 px-1 py-1 ${cellClass(r)} ${c.right ? "text-right" : ""}`}>
                      {editor(r, c)}
                    </td>
                  ))}
                  <td className="px-2">{statusBadge(r)}</td>
                  <td className="px-1">{rowActions(r)}</td>
                </tr>
              ))}
              {end < filtered.length && <tr style={{ height: (filtered.length - end) * ROW_H }} aria-hidden />}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
          <span data-testid="virtual-window-label">
            Rows {filtered.length ? start + 1 : 0}–{end} of {filtered.length} rendered (virtualized)
          </span>
          <span>Scroll the table to load more rows instantly</span>
        </div>
      </div>

      {/* mobile cards */}
      <div className="space-y-3 md:hidden" data-testid="transaction-cards-mobile">
        {mobileRows.map((r) => (
          <div key={r.id} className={`rounded-lg border border-border bg-white p-3 ${r.confidence_band === "Low" ? "bg-warn" : ""} ${r.excluded ? "opacity-60" : ""}`}
               data-testid={`txn-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className={`truncate text-sm font-medium ${r.excluded ? "line-through" : ""}`}>{r.description || "—"}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(r.date)} · {r.transaction_type}</p>
              </div>
              <div className="flex items-center gap-1">
                <p className={`num text-sm font-semibold ${r.credit > 0 ? "text-green-700" : "text-red-700"}`}>
                  {r.credit > 0 ? `+${inr(r.credit)}` : `-${inr(r.debit)}`}
                </p>
                {rowActions(r)}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Select value={r.category || ""} onValueChange={(v) => setCell(r.id, "category", v)}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid={`m-cell-category-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {FLAT_CATEGORIES.map(({ c }) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">Bal {inr(r.balance)}</span>
              <Badge variant="outline" className="text-[10px]">{Math.round((r.confidence || 0) * 100)}%</Badge>
              {statusBadge(r)}
            </div>
          </div>
        ))}
        {mobileCount < filtered.length && (
          <Button variant="outline" className="w-full bg-white" data-testid="mobile-load-more-btn"
                  onClick={() => setMobileCount((c) => c + MOBILE_CHUNK)}>
            Load more ({filtered.length - mobileCount} remaining)
          </Button>
        )}
      </div>
    </div>
  );
};
